/**
 * useAttentionActions (KAN-628, KAN-726)
 *
 * Closing a row of the "Требуют внимания" card: "Взять в работу" (action done, KAN-852),
 * "Успех" and "Отказ" (outcome, the backend moves the client in the funnel).
 * Every action removes the row optimistically from the cached list of the open
 * tab and returns it when the request fails. Outcomes also refresh the funnel
 * block (KAN-737). KAN-734: the same actions serve the "Отработанные" view; its
 * queries live under ATTENTION_QUERY_KEY, so any action refreshes both views.
 * KAN-749: a failed request shows a toast (the row comes back as before), and
 * a second click on a row whose request is in flight is ignored, so a double
 * click sends one request.
 * KAN-750: "Взять в работу" carries the manager chosen in the dialog (`managerId`,
 * null: no manager); after a successful request the clients and managers
 * queries are refreshed too.
 * KAN-753: `successMessage` (done with the session manager, no dialog) is shown
 * as a success toast after the request succeeds.
 * KAN-756: the cached data shape comes with `applyAction` (the dashboard list
 * uses attentionListRemover(channel), the CRM board its own remover).
 * KAN-787: AttentionAction lives in services/api/dashboard (re-exported here).
 * KAN-897: in-flight refetches of `queryKey` are cancelled before the
 * optimistic removal.
 * KAN-955: "Работа идёт" (work_continues) closes an idle row the same way
 * (dashboardApi.markWorkContinues); it does not touch the funnel.
 */
import { useCallback, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  dashboardApi,
  type AttentionAction,
  type AttentionChannel,
  type AttentionList,
  type AttentionOutcome,
} from '@/services/api/dashboard'
import {
  ATTENTION_QUERY_KEY,
  FUNNEL_STAGES_QUERY_KEY,
  MANAGERS_QUERY_KEY,
} from '@/services/api/queryKeys'
import { clientQueryKey } from '@/services/api/clientQueries'
import { attentionActionErrorMessage } from './attentionActionError'

export type { AttentionAction } from '@/services/api/dashboard'

interface AttentionActionVars {
  clientId: string
  action: AttentionAction
  /** KAN-750: manager for "Взять в работу" (null: none); undefined: not touched. */
  managerId?: string | null
  /** KAN-753: success toast text; undefined: no toast. */
  successMessage?: string
}

const isOutcome = (action: AttentionAction): action is AttentionOutcome =>
  action === 'success' || action === 'refused'

function send({ clientId, action, managerId }: AttentionActionVars): Promise<void> {
  // KAN-955: "Работа идёт" only moves the start of the idle countdown.
  if (action === 'work_continues') return dashboardApi.markWorkContinues(clientId)
  if (isOutcome(action)) return dashboardApi.setAttentionOutcome(clientId, action)
  return managerId === undefined
    ? dashboardApi.markAttentionDone(clientId)
    : dashboardApi.markAttentionDone(clientId, managerId)
}

/**
 * KAN-756: optimistic removal of a row from the cached data under `queryKey`.
 * KAN-786: the action comes along, so the CRM board can also set the outcome.
 * KAN-787: renamed from RemoveClient, it applies the action, not only removes.
 */
export type ApplyAttentionAction<T> = (data: T, clientId: string, action: AttentionAction) => T

function withoutClient(
  list: AttentionList,
  clientId: string,
  channel: AttentionChannel
): AttentionList {
  return {
    ...list,
    total: Math.max(0, list.total - 1),
    items: list.items.filter((item) => item.client_id !== clientId),
    totals: list.totals && {
      ...list.totals,
      [channel]: Math.max(0, list.totals[channel] - 1),
    },
  }
}

/** Remover for the dashboard list of one channel tab. */
export function attentionListRemover(
  channel: AttentionChannel
): ApplyAttentionAction<AttentionList> {
  return (list, clientId) => withoutClient(list, clientId, channel)
}

/**
 * KAN-834: remover for a list of all channels (the manager app): the row and
 * the total go, per-channel totals are not shown there and stay as they are.
 */
export const attentionRowRemover: ApplyAttentionAction<AttentionList> = (list, clientId) => {
  const items = list.items.filter((item) => item.client_id !== clientId)
  // The client card can close a client that is not in the list: the total stays.
  const removed = list.items.length - items.length
  return { ...list, total: Math.max(0, list.total - removed), items }
}

export function useAttentionActions<T = AttentionList>(
  queryKey: readonly unknown[],
  applyAction: ApplyAttentionAction<T>
) {
  const queryClient = useQueryClient()
  // Ref guards against a double click before React re-renders; state drives
  // the disabled buttons of every row whose request is in flight.
  const inFlight = useRef(new Set<string>())
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(() => new Set())

  const setPending = useCallback((clientId: string, pending: boolean) => {
    if (pending) inFlight.current.add(clientId)
    else inFlight.current.delete(clientId)
    setPendingIds(new Set(inFlight.current))
  }, [])

  const mutation = useMutation({
    mutationFn: send,
    onMutate: async ({ clientId, action }: AttentionActionVars) => {
      await queryClient.cancelQueries({ queryKey: ATTENTION_QUERY_KEY })
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<T>(queryKey)
      if (previous) {
        queryClient.setQueryData<T>(queryKey, applyAction(previous, clientId, action))
      }
      return { previous }
    },
    onError: (error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
      toast.error(attentionActionErrorMessage(error))
    },
    onSuccess: (_data, vars) => {
      if (vars.successMessage) toast.success(vars.successMessage)
      if (vars.managerId === undefined) return
      for (const key of [['clients'], clientQueryKey(vars.clientId), MANAGERS_QUERY_KEY]) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
    onSettled: (_data, _error, vars) => {
      setPending(vars.clientId, false)
      queryClient.invalidateQueries({ queryKey: ATTENTION_QUERY_KEY })
      if (isOutcome(vars.action)) {
        queryClient.invalidateQueries({ queryKey: FUNNEL_STAGES_QUERY_KEY })
      }
    },
  })

  const { mutate } = mutation
  const run = useCallback(
    (
      clientId: string,
      action: AttentionAction,
      managerId?: string | null,
      successMessage?: string
    ) => {
      if (inFlight.current.has(clientId)) return
      setPending(clientId, true)
      const vars: AttentionActionVars = { clientId, action }
      if (managerId !== undefined) vars.managerId = managerId
      if (successMessage) vars.successMessage = successMessage
      mutate(vars)
    },
    [mutate, setPending]
  )

  return {
    run,
    /** True while a request for this client is in flight (its buttons are disabled). */
    isPending: (clientId: string) => pendingIds.has(clientId),
  }
}
